import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Badge, Button, Checkbox, Group, Loader, Modal,
  Select, Stack, Text,
} from '@mantine/core';
import { useAppContext } from '../contexts/AppContext';
import { sprekerKleur, sprekerLabel, uniekeSprekers } from '../utils/sprekers';

const JOB_LABEL = {
  wachtend: { kleur: 'yellow', tekst: 'Transcriptie wacht op de transcriptieserver' },
  bezig: { kleur: 'blue', tekst: 'Transcriptie is bezig…' },
  klaar: { kleur: 'green', tekst: 'Transcriptie gereed' },
  mislukt: { kleur: 'red', tekst: 'Transcriptie mislukt' },
};

// Review van het sessietranscript: tekst gegroepeerd per aaneengesloten
// station+versie, met sprekerskleuren; controle per blok; ook ná afronden te
// bekijken. Kop met belangentafel-nummer, datum/tijd en aanwezigen.
export default function TranscriptReview({ sessie, geopend, onSluit, onAfgerond }) {
  const { fetchWithAuth, addToast } = useAppContext();
  const [segmenten, setSegmenten] = useState([]);
  const [opname, setOpname] = useState(null);
  const [projecten, setProjecten] = useState([]);
  // Locaties (plaatsingsversies) per station, voor de per-blok locatiekeuze.
  const [versiesPerProject, setVersiesPerProject] = useState({});
  const [deelnemers, setDeelnemers] = useState([]);
  const [laden, setLaden] = useState(false);
  const [bezig, setBezig] = useState(false);

  const readOnly = sessie?.status === 'afgerond';

  const laad = useCallback(async () => {
    if (!sessie) return;
    setLaden(true);
    try {
      const res = await fetchWithAuth(`/api/sessies/${sessie.id}/transcript`);
      const data = await res.json();
      if (data.success) {
        setSegmenten(data.segmenten);
        setOpname(data.opname);
        // Locaties per betrokken station ophalen voor de per-blok locatiekeuze
        const pnrs = [...new Set((data.segmenten || []).map(s => s.projectnummer).filter(Boolean))];
        const paren = await Promise.all(pnrs.map(async pnr => {
          try {
            const r = await fetchWithAuth(`/api/projecten/${encodeURIComponent(pnr)}/versies`);
            const d = await r.json();
            return [pnr, d.success ? d.versies.filter(v => !v.vluchtig) : []];
          } catch (e) { return [pnr, []]; }
        }));
        setVersiesPerProject(Object.fromEntries(paren));
      }
      const resDetail = await fetchWithAuth(`/api/sessies/${sessie.id}`);
      const dataDetail = await resDetail.json();
      if (dataDetail.success) setDeelnemers(dataDetail.deelnemers || []);
      const resProj = await fetchWithAuth(`/api/projecten?buurtcode=${encodeURIComponent(sessie.buurtcode)}`);
      const dataProj = await resProj.json();
      setProjecten((dataProj.features || []).map(f => f.properties.projectnummer).filter(Boolean));
    } catch (e) {
      addToast && addToast('Transcript laden mislukt', 'error');
    } finally {
      setLaden(false);
    }
  }, [sessie, fetchWithAuth, addToast]);

  useEffect(() => { if (geopend) laad(); }, [geopend, laad]);

  const wijzigSegmenten = async (ids, velden) => {
    setSegmenten(prev => prev.map(s => (ids.includes(s.id) ? { ...s, ...velden } : s)));
    try {
      for (const id of ids) {
        const res = await fetchWithAuth(`/api/transcript_segmenten/${id}`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(velden),
        });
        const data = await res.json();
        if (!data.success) throw new Error(data.error);
      }
    } catch (e) {
      addToast && addToast('Opslaan mislukt', 'error');
      laad();
    }
  };

  const herkoppel = async () => {
    setBezig(true);
    try {
      const res = await fetchWithAuth(`/api/sessies/${sessie.id}/herkoppel`, { method: 'POST' });
      const data = await res.json();
      if (data.success) { addToast && addToast('Opnieuw gekoppeld', 'success'); laad(); }
      else addToast && addToast(data.error || 'Herkoppelen mislukt', 'error');
    } catch (e) {
      addToast && addToast('Herkoppelen mislukt', 'error');
    } finally {
      setBezig(false);
    }
  };

  const keurAllesGoed = async () => {
    setBezig(true);
    try {
      const ids = segmenten.filter(s => s.status !== 'gecontroleerd').map(s => s.id);
      if (ids.length) await wijzigSegmenten(ids, { status: 'gecontroleerd' });
      addToast && addToast('Alle fragmenten gecontroleerd', 'success');
    } finally {
      setBezig(false);
    }
  };

  const rondAf = async () => {
    setBezig(true);
    try {
      const res = await fetchWithAuth(`/api/sessies/${sessie.id}/afronden`, { method: 'POST' });
      const data = await res.json();
      if (!data.success) { addToast && addToast(data.error || 'Afronden mislukt', 'error'); return; }
      addToast && addToast('Sessie afgerond — de audio-opname is verwijderd', 'success');
      onAfgerond && onAfgerond();
      onSluit();
    } catch (e) {
      addToast && addToast('Afronden mislukt', 'error');
    } finally {
      setBezig(false);
    }
  };

  if (!sessie) return null;
  const job = opname ? JOB_LABEL[opname.job_status] : null;
  const allesGecontroleerd = segmenten.length > 0 && segmenten.every(s => s.status === 'gecontroleerd');

  // Sprekers op volgorde van eerste voorkomen (kleur + "Spreker N")
  const sprekers = uniekeSprekers(segmenten);

  // Aaneengesloten segmenten van hetzelfde station+versie tot één blok groeperen
  const blokken = [];
  segmenten.forEach(seg => {
    const key = `${seg.projectnummer || '__buurt__'}|${seg.lokatie_versie_id || ''}`;
    const laatste = blokken[blokken.length - 1];
    if (laatste && laatste.key === key) laatste.segmenten.push(seg);
    else blokken.push({
      key, projectnummer: seg.projectnummer, lokatie_versie_id: seg.lokatie_versie_id,
      versie_nr: seg.versie_nr, segmenten: [seg],
    });
  });

  const datumTijd = sessie.gestart_op
    ? new Date(sessie.gestart_op).toLocaleString('nl-NL', { dateStyle: 'medium', timeStyle: 'short' })
    : '';

  return (
    <Modal
      opened={geopend} onClose={onSluit} size="xl"
      title={`${readOnly ? 'Transcript' : 'Review'} — Belangentafel ${sessie.belangentafel_nr || sessie.id}`}
    >
      <Stack gap="sm">
        {laden && <Group justify="center" p="md"><Loader size="sm" /></Group>}

        {!laden && (
          <Stack gap={4} style={{ borderBottom: '1px solid var(--mantine-color-gray-2)', paddingBottom: 8 }}>
            <Text size="sm" fw={600}>
              Belangentafel {sessie.belangentafel_nr || sessie.id}{datumTijd ? ` · ${datumTijd}` : ''}
            </Text>
            {deelnemers.length > 0 && (
              <Group gap={6}>
                <Text size="xs" c="dimmed">Aanwezig:</Text>
                {deelnemers.map((d, i) => (
                  <Badge key={i} size="sm" variant="light" color="gray" tt="none">
                    {d.naam}{d.organisatie ? ` (${d.organisatie})` : ''}
                  </Badge>
                ))}
              </Group>
            )}
          </Stack>
        )}

        {!laden && !opname && (
          <Alert color="yellow">Er is geen opname voor deze sessie geüpload.</Alert>
        )}
        {!laden && job && opname.job_status !== 'klaar' && (
          <Alert color={job.kleur}>
            {job.tekst}
            {opname.foutmelding && <Text size="xs" mt={4}>{opname.foutmelding} (poging {opname.pogingen})</Text>}
            <Button size="xs" variant="light" mt="xs" onClick={laad}>Verversen</Button>
          </Alert>
        )}

        {!laden && segmenten.length > 0 && (
          <>
            {sprekers.length > 0 && (
              <Group gap="xs">
                {sprekers.map(sp => (
                  <Badge key={sp} variant="light" tt="none"
                    styles={{ root: { color: sprekerKleur(sp, sprekers), borderColor: sprekerKleur(sp, sprekers) } }}>
                    {sprekerLabel(sp, sprekers)}
                  </Badge>
                ))}
              </Group>
            )}
            {!readOnly && (
              <Text size="sm" c="dimmed">
                De tekst is per station gegroepeerd. Klopt een koppeling niet? Kies
                het juiste station, of gebruik "Opnieuw koppelen". Vink een station
                af als het gecontroleerd is.
              </Text>
            )}

            {blokken.map((blok, bi) => {
              const ids = blok.segmenten.map(s => s.id);
              const blokGecontroleerd = blok.segmenten.every(s => s.status === 'gecontroleerd');
              return (
                <Stack key={bi} gap={4}
                  style={{ borderLeft: `3px solid ${blokGecontroleerd ? 'var(--mantine-color-groen-6)' : 'var(--mantine-color-gray-3)'}`, paddingLeft: 10 }}>
                  <Group justify="space-between" wrap="nowrap">
                    <Group gap={6} wrap="nowrap" style={{ minWidth: 0 }}>
                      {readOnly ? (
                        <Text fw={600} size="sm">{blok.projectnummer || 'Buurt (geen station)'}</Text>
                      ) : (
                        <Select
                          size="xs" placeholder="Buurt (geen station)" clearable searchable
                          data={projecten} value={blok.projectnummer}
                          onChange={v => wijzigSegmenten(ids, { projectnummer: v })}
                          style={{ width: 200 }}
                        />
                      )}
                      {blok.projectnummer && (versiesPerProject[blok.projectnummer] || []).length > 0 && !readOnly ? (
                        <Select
                          size="xs" placeholder="locatie" clearable
                          data={(versiesPerProject[blok.projectnummer] || []).map(v => ({
                            value: String(v.id), label: `locatie ${v.versie_nr}${v.gekozen ? ' ★' : ''}`,
                          }))}
                          value={blok.lokatie_versie_id != null ? String(blok.lokatie_versie_id) : null}
                          onChange={val => {
                            const vid = val ? Number(val) : null;
                            const versie = (versiesPerProject[blok.projectnummer] || []).find(v => v.id === vid);
                            wijzigSegmenten(ids, { lokatie_versie_id: vid, versie_nr: versie ? versie.versie_nr : null });
                          }}
                          style={{ width: 130 }}
                        />
                      ) : (blok.versie_nr != null && (
                        <Badge size="sm" variant="light" color="blue">locatie {blok.versie_nr}</Badge>
                      ))}
                    </Group>
                    <Checkbox
                      size="xs" label="Gecontroleerd" checked={blokGecontroleerd} disabled={readOnly}
                      onChange={e => wijzigSegmenten(ids, { status: e.currentTarget.checked ? 'gecontroleerd' : 'concept' })}
                    />
                  </Group>
                  <Text size="sm" style={{ lineHeight: 1.6 }}>
                    {blok.segmenten.map((seg, si) => (
                      <Text key={si} span style={{ color: sprekerKleur(seg.spreker, sprekers) }}>
                        {seg.tekst}{' '}
                      </Text>
                    ))}
                  </Text>
                </Stack>
              );
            })}

            {!readOnly && (
              <Group justify="space-between" mt="sm">
                <Group gap="xs">
                  <Button variant="default" onClick={herkoppel} loading={bezig}>Opnieuw koppelen</Button>
                  <Button variant="default" onClick={keurAllesGoed} loading={bezig}
                    disabled={allesGecontroleerd}>Alles goedkeuren</Button>
                </Group>
                <Button color="groen" onClick={rondAf} loading={bezig} disabled={!allesGecontroleerd}>
                  Sessie afronden (audio wordt verwijderd)
                </Button>
              </Group>
            )}
          </>
        )}
      </Stack>
    </Modal>
  );
}
